// To book › Find a lead (BL-092, AdTrack's Lead Manager): every lead, searched by name, phone, email or
// what they asked, open, booked or closed, or only those from an ad; newest first, a page at a time with
// how many there are (leads.search). Each opens to the ad click it came from. Its own part beside the
// leads list, which keeps its own paging.

import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { AdClickDetails } from '../shared/AdClick.tsx';
import type { AdClick } from '../shared/ad-click-model.ts';
import { phoneText } from '../types.ts';
import { searchAsked, searchCount, type SearchStatus } from './lead-search-model.ts';
import { sourceLabel, type Lead } from './tobook-model.ts';
import './lead-search.css';

interface Found {
  id: string;
  source: Lead['source'];
  status: 'open' | 'booked' | 'closed';
  name: string | null;
  phone: string | null;
  email: string | null;
  summary: string;
  createdAt: string;
  click: AdClick | null;
}
interface Page {
  leads: Found[];
  total: number;
  next: string | null;
}
const PAGE = 25;
const STATUS_WORDS: Record<Found['status'], string> = { open: 'Open', booked: 'Booked', closed: 'Closed' };

export function LeadSearch({ timeZone }: { timeZone: string }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<SearchStatus>('all');
  const [fromAds, setFromAds] = useState(false);
  const [found, setFound] = useState<Page | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const asked = searchAsked(q, fromAds);
  useEffect(() => {
    setFound(null);
    setError(null);
    if (!asked) return;
    let live = true;
    // a moment after the last key, so each letter isn't a search
    const t = setTimeout(() => {
      client
        .call<Page>('leads.search', { q: q.trim(), status, fromAds, limit: PAGE })
        .then((r) => live && setFound(r))
        .catch((e: unknown) => live && setError(errorText(e, 'Could not search the leads.')));
    }, 300);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q, status, fromAds, asked]);
  const showMore = async () => {
    if (!found?.next) return;
    setMore(true);
    try {
      const r = await client.call<Page>('leads.search', {
        q: q.trim(),
        status,
        fromAds,
        limit: PAGE,
        after: found.next,
      });
      setFound({ leads: [...found.leads, ...r.leads], total: r.total, next: r.next });
    } catch (e) {
      setError(errorText(e, 'Could not get more leads.'));
    } finally {
      setMore(false);
    }
  };
  const when = (iso: string) =>
    new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  return (
    <section className="sect lead-search" aria-labelledby="lead-search-title">
      <h2 id="lead-search-title">Find a lead</h2>
      <div className="lead-find" role="search">
        <input
          className="field"
          type="search"
          aria-label="Name, phone, email or what they asked"
          placeholder="Name, phone, email or what they asked"
          maxLength={100}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="field"
          aria-label="Which leads"
          value={status}
          onChange={(e) => setStatus(e.target.value as SearchStatus)}
        >
          <option value="all">Open, booked and closed</option>
          <option value="open">Open</option>
          <option value="booked">Booked</option>
          <option value="closed">Closed</option>
        </select>
        <label className="check">
          <input type="checkbox" checked={fromAds} onChange={(e) => setFromAds(e.target.checked)} />
          From an ad
        </label>
      </div>
      {error && <p className="error">{error}</p>}
      {asked && !found && !error && <p className="muted">Searching…</p>}
      {found && (
        <>
          <p className="muted small" role="status">
            {searchCount(found.total, found.leads.length)}
          </p>
          {found.leads.length > 0 && (
            <ul className="rows" aria-label="Leads found">
              {found.leads.map((l) => (
                <li key={l.id} className="stone mat lead">
                  <div>
                    <b>{l.name ?? (l.phone ? phoneText(l.phone) : (l.email ?? 'No name'))}</b>
                    <p className="muted small">
                      {sourceLabel(l.source)} · {when(l.createdAt)}
                      {l.name && l.phone ? ` · ${phoneText(l.phone)}` : ''}
                      {l.email && (l.name || l.phone) ? ` · ${l.email}` : ''}
                    </p>
                    <p className="lead-what">{l.summary}</p>
                    {l.click && <AdClickDetails click={l.click} timeZone={timeZone} />}
                  </div>
                  <span className={`chip say${l.status === 'booked' ? ' ok' : ''}`}>
                    {STATUS_WORDS[l.status]}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {found.next && (
            <button type="button" className="lead-more" disabled={more} onClick={() => void showMore()}>
              {more ? 'Getting more…' : 'Show more'}
            </button>
          )}
        </>
      )}
    </section>
  );
}
